import React, {useEffect} from 'react';

import {useStore} from 'effector-react/effector-react.cjs';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory, useLocation} from 'react-router-dom';

import {Page} from 'components/common/Page';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Link} from 'components/ui-kit/Link';
import {ROUTE} from 'core/routes';
import {tableState$} from 'models/tracing/grid/table';
import {
    GridFieldConfig,
    TracingGridAccessors,
    TracingGridFormattedRow,
    TracingGridRowRaw,
} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {formatNumber} from 'utils/number';

import {ControlsWrapper, HeaderControls} from './styled';
import {TracingGridTable} from './Table';
import {Period} from './Table/Period';
import {TableSearch} from './Table/SearchFilter';

export function convertData(
    data: TracingGridRowRaw[] | undefined,
    search: string,
    t: TFunction,
    isOperator: boolean,
): TracingGridFormattedRow[] {
    if (!data) {
        return [];
    }
    return data.map((elem) => ({
        ...(isOperator && {
            [TracingGridAccessors.seller]: elem.seller.name,
        }),
        [TracingGridAccessors.name]: (
            <Link
                to={{
                    pathname: generatePath(ROUTE.TRACING_DETAIL, {
                        id: elem.seller.clientId,
                        gtin: elem.gtin,
                    }),
                    search,
                }}
            >
                {elem.product.name}
            </Link>
        ),
        [TracingGridAccessors.gtin]: handleNil(elem.gtin),
        [TracingGridAccessors.trademark]: handleNil(elem.product.brand),
        [TracingGridAccessors.codeGroup]: handleNil(elem.product.group, (value) => (
            <ProductGroupIcon value={value} label />
        )),
        [TracingGridAccessors.qnt]: handleNil(elem.quantity, (value: number) =>
            formatNumber(value),
        ),
        [TracingGridAccessors.avgPrice]: handleNil(elem.averagePrice, (value: number) =>
            formatNumber(value),
        ),
    }));
}

const getGridColumns = (t: TFunction, isOperator: boolean): GridFieldConfig[] => {
    const items: GridFieldConfig[] = [
        {
            id: TracingGridAccessors.name,
            header: t('pages.tracing.name'),
            isSortable: true,
        },
        {
            id: TracingGridAccessors.gtin,
            header: t('pages.tracing.gtin'),
            isSortable: true,
        },
        {
            id: TracingGridAccessors.trademark,
            header: t('pages.tracing.trademark'),
            isSortable: true,
        },
        {
            id: TracingGridAccessors.codeGroup,
            header: t('pages.tracing.groupCode'),
            isSortable: true,
            width: 150,
        },
        {
            id: TracingGridAccessors.qnt,
            header: t('pages.tracing.qnt'),
            isSortable: true,
        },
        {
            id: TracingGridAccessors.avgPrice,
            header: t('pages.tracing.avgPrice'),
            isSortable: true,
        },
    ];

    if (isOperator)
        items.unshift({
            id: TracingGridAccessors.seller,
            header: t('pages.tracing.participant'),
            isSortable: false,
        });

    return items;
};

export const TracingPage = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const {search} = useLocation();
    const {period} = useStore(tableState$);

    useEffect(() => {
        const [from, to] = period;
        const params = new URLSearchParams({
            ...(from && {from: formatDate(from, 'yyyy-MM-dd')}),
            ...(to && {to: formatDate(to, 'yyyy-MM-dd')}),
        });
        history.replace({search: params.toString()});
    }, [period, search]);

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
            </ControlsWrapper>
            <ControlsWrapper>
                <Period range={period} />
            </ControlsWrapper>
        </HeaderControls>
    );

    return (
        <Page title={t('menu.Tracing')}>
            <TracingGridTable
                configFunc={getGridColumns}
                dataMapper={convertData}
                headerControls={headerControls}
            />
        </Page>
    );
};
